export type Strategy = {
  strategy: string;
  slug?: string;
  subcategory: string;
  skill_level: string;
  market_conditions: string;
  risk: string;
  profit: string;
  loss: string;
  capital_requirement: string;
  capital_required_rationale: string;
  structure: string;
  description: {
    Overview: string[];
    Usage: string[];
    'Risk Profile': string[];
    Considerations: string[];
  };
  other_names: string;
  delta_sensitivity: string;
  delta_rationale: string;
  gamma_sensitivity: string;
  gamma_rationale: string;
  theta_sensitivity: string;
  theta_impact: string;
  theta_rationale: string;
  vega_sensitivity: string;
  vega_rationale: string;
  rho_sensitivity: string;
  rho_rationale: string;
};

export const allStrategies: Strategy[] = [
  {
    strategy: 'Bear Call Ladder',
    slug: 'bear-call-ladder',
    subcategory: 'Ladder Spread',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Involves buying higher strike price calls, hence higher capital.',
    structure:
      '- Sell a call at a lower strike\n-  Buy a call at a higher strike\n- Buy a call at an even higher strike',
    description: {
      Overview: [
        'A multi-leg, bearish strategy designed for experienced traders.',
        'Involves selling one call at a lower strike, buying one at a higher strike, and selling another call at an even higher strike.',
        'Targets scenarios where the stock price is expected to drop or remain stagnant.',
      ],
      Usage: [
        'Best employed in markets with bearish or neutral trends.',
        'Suited for traders seeking to capitalize on downward stock movements while limiting risk.',
        'Aims to generate income while managing risk through a structured approach.',
      ],
      'Risk Profile': [
        'Offers a favorable risk-reward balance with limited loss potential and unlimited profit potential.',
        'Less risky compared to single-leg bearish strategies.',
        'Risk management includes strategic strike selection and monitoring of stock movement.',
      ],
      Considerations: [
        'Requires understanding of time decay and options liquidity.',
        'Key factors include stock price direction and volatility.',
        'Advice: Be cautious of unexpected bullish trends; know when to adjust or exit the strategy.',
      ],
    },
    other_names: 'Short Call Ladder',
    delta_sensitivity: 'High',
    delta_rationale: 'Involves multiple call options, high exposure to underlying moves.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Multiple legs lead to varied gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Mix of short and long calls; time decay varies.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Mix of short and long calls balances Vega.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short calls dominate, reducing Rho impact.',
  },
  {
    strategy: 'Bear Call Spread',
    slug: 'bear-call-spread',
    subcategory: 'Credit Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Bearish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Low',
    capital_required_rationale: 'Credit spread; receives premium upfront.',
    structure: '- Sell a call at a lower strike\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'A credit spread strategy for moderately skilled traders.',
        'Comprises selling a call at a lower strike and buying a call at a higher strike.',
        'Intended for scenarios predicting a mild decline in stock price.',
      ],
      Usage: [
        'Ideal for moderately bearish market conditions.',
        'Suitable for investors seeking to profit from small downward movements.',
        'Used for income generation with controlled risk.',
      ],
      'Risk Profile': [
        'Balanced risk-reward with both profit and loss potential being limited.',
        'Safer than aggressive bearish strategies.',
        'Risk control through selection of strike prices and expiration dates.',
      ],
      Considerations: [
        "Be aware of time decay's impact, especially near expiration.",
        'Influenced by stock price trends and volatility levels.',
        'Tip: Avoid in highly volatile markets; monitor for unexpected bullish reversals.',
      ],
    },
    other_names: 'Call Credit Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Spread strategy balances delta exposure between bought and sold calls.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Short and long calls balance gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Credit spread; benefits from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short call position reduces Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short call position, low Rho sensitivity.',
  },
  {
    strategy: 'Bear Put Ladder',
    slug: 'bear-put-ladder',
    subcategory: 'Ladder Spread',
    skill_level: 'Advanced',
    market_conditions: 'Income, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale:
      'Buying puts at different strike prices requires significant capital.',
    structure:
      '- Sell a put at a lower strike\n- Sell a put at an even lower strike\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A complex, multi-leg strategy for seasoned traders.',
        'Involves selling two puts at different strikes and buying one at a higher strike.',
        'Targets neutral to slightly bearish market outlooks.',
      ],
      Usage: [
        'Best used in neutral markets or where slight bearishness is anticipated.',
        'Ideal for investors seeking to profit from small price declines.',
        'Aims for income generation with a focus on risk management.',
      ],
      'Risk Profile': [
        'High risk with nearly unlimited loss potential but limited profit potential.',
        'Higher risk compared to single-leg bearish strategies.',
        'Managing risks involves careful selection of strike prices and active monitoring.',
      ],
      Considerations: [
        'Requires a solid understanding of options pricing and market trends.',
        'Sensitive to stock movement and volatility.',
        'Caution: High risk requires vigilant management and timely adjustments.',
      ],
    },
    other_names: 'Long Put Ladder',
    delta_sensitivity: 'High',
    delta_rationale: 'Multiple put options, inversely sensitive to stock price rise.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Varied strikes and legs lead to moderate gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Negative',
    theta_rationale: 'Long puts affected by time decay; offset by shorts.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Combination of long and short puts.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long puts present, but offset by short puts.',
  },
  {
    strategy: 'Bear Put Spread',
    slug: 'bear-put-spread',
    subcategory: 'Debit Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Bearish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Debit spread; involves buying and selling puts.',
    structure: '- Sell a put at a lower strike\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A debit spread strategy for intermediate traders.',
        'Comprises buying a put at a higher strike and selling one at a lower strike.',
        'Suitable for expecting a moderate decline in stock prices.',
      ],
      Usage: [
        ' Ideal in moderately bearish market environments.',
        'Suitable for investors seeking profit from controlled bearish movements.',
        'Used for risk-managed income generation.',
      ],
      'Risk Profile': [
        'Offers a balanced risk-reward ratio with both profit and loss being capped.',
        ' Lower risk compared to more aggressive bearish positions.',
        ' Risk control through strategic strike and expiration selection.',
      ],
      Considerations: [
        'Important to understand the impact of time decay and volatility.',
        ' Dependent on accurate prediction of stock price declines.',
        'Practical tip: Monitor market trends closely; manage risks effectively.',
      ],
    },
    other_names: 'Put Debit Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Combination of long and short puts balances delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced gamma due to spread structure.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Debit spread; suffers from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Long put position increases Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long puts have some Rho sensitivity.',
  },
  {
    strategy: 'Bull Call Ladder',
    slug: 'bull-call-ladder',
    subcategory: 'Ladder Spread',
    skill_level: 'Advanced',
    market_conditions: 'Income, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Involves buying calls, higher strike prices require more capital.',
    structure:
      '- Buy a call at a lower strike\n- Sell a call at a higher strike\n- Sell a call at an even higher strike',
    description: {
      Overview: [
        'An advanced strategy combining buying and selling calls at different strikes.',
        'Involves buying a call at a lower strike, selling a call at a higher strike, and selling another call at an even higher strike.',
        'Targets scenarios where the stock price is expected to rise moderately or remain stable.',
      ],
      Usage: [
        'Best in markets with neutral to slightly bullish trends.',
        'Suited for traders looking to profit from moderate stock gains while minimizing costs.',
        'Strategic for generating income with controlled risk exposure.',
      ],
      'Risk Profile': [
        'Potential for limited profits but unlimited risk.',
        'More risk compared to simple bullish strategies due to additional sold calls.',
        'Risk managed through careful selection of strike prices and monitoring stock movement.',
      ],
      Considerations: [
        'Requires a good grasp of time decay and options liquidity.',
        'Influenced by stock price trajectory and market volatility.',
        'Caution advised against unexpected bearish trends and managing open-ended risk.',
      ],
    },
    other_names: 'Long Call Ladder',
    delta_sensitivity: 'High',
    delta_rationale: 'Involves multiple call options, sensitive to underlying price increase.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Different strike prices create moderate gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Negative',
    theta_rationale: 'Long calls lose value over time; offset by shorts.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Mix of long and short calls.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Presence of long calls increases Rho impact.',
  },
  {
    strategy: 'Bull Call Spread',
    slug: 'bull-call-spread',
    subcategory: 'Debit Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Bullish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Debit spread; buying and selling calls.',
    structure: '- Buy a call at a lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A debit spread strategy for moderately skilled traders.',
        'Involves buying a call at a lower strike and selling a call at a higher strike.',
        'Suitable for scenarios expecting a moderate rise in stock prices.',
      ],
      Usage: [
        'Ideal in moderately bullish market conditions.',
        'Favored by investors aiming to profit from modest upward stock movements.',
        'Utilized for risk-controlled income generation.',
      ],
      'Risk Profile': [
        'Balanced risk-reward profile with capped profit and loss.',
        'Lower risk compared to more aggressive bullish strategies.',
        'Risk managed by strategic strike and expiration date selection.',
      ],
      Considerations: [
        'Important to account for the impact of time decay and volatility.',
        'Reliant on accurate prediction of modest stock price increases.',
        'Advisable to monitor market trends and manage risks effectively.',
      ],
    },
    other_names: 'Call Debit Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Long and short call position moderates delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Spread structure results in low gamma risk.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Debit spread; time decay erodes value.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Long call position increases Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long calls increase Rho sensitivity.',
  },
  {
    strategy: 'Bull Put Ladder',
    slug: 'bull-put-ladder',
    subcategory: 'Ladder Spread',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Selling puts at different strikes can require significant margin.',
    structure:
      '- Buy a put at a lower strike\n- Buy a put at an even lower strike\n- Sell a put at a higher strike',
    description: {
      Overview: [
        'A complex, multi-leg strategy for experienced traders.',
        'Involves selling two puts at different strikes and buying one put at a lower strike.',
        'Aims at neutral to slightly bullish market outlooks.',
      ],
      Usage: [
        'Best utilized in neutral or slightly bullish markets.',
        'Ideal for traders seeking to profit from small price increases.',
        'Aims for income generation with emphasis on risk management.',
      ],
      'Risk Profile': [
        'High-risk strategy with nearly unlimited loss potential but limited profit.',
        'Riskier than single-leg bullish strategies due to additional sold puts.',
        'Requires vigilant strike selection and market monitoring for risk management.',
      ],
      Considerations: [
        'Demands a solid understanding of options pricing and market movements.',
        'Highly sensitive to stock direction and volatility.',
        'Caution: High risk necessitates active management and timely strategy adjustments.',
      ],
    },
    other_names: 'Short Put Ladder',
    delta_sensitivity: 'High',
    delta_rationale: 'Multiple put options, sensitive to decreases in underlying price.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Varied put strikes create moderate gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short puts benefit from time decay; offset by longs.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Short puts reduce, long puts increase Vega.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short puts dominate, reducing Rho impact.',
  },
  {
    strategy: 'Bull Put Spread',
    slug: 'bull-put-spread',
    subcategory: 'Credit Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Bullish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Low',
    capital_required_rationale: 'Credit spread; receives premium upfront.',
    structure: '- Buy a put at a lower strike\n- Sell a put at a higher strike',
    description: {
      Overview: [
        'A credit spread strategy for traders with moderate experience.',
        'Involves selling a put at a higher strike and buying a put at a lower strike.',
        'Targeted at scenarios predicting a modest increase in stock prices.',
      ],
      Usage: [
        'Ideal in moderately bullish market environments.',
        'Suitable for investors looking to profit from controlled bullish movements.',
        'Used for generating income with a managed risk profile.',
      ],
      'Risk Profile': [
        'Offers a balanced risk-reward ratio with capped profit and loss.',
        'Less risky compared to aggressive bullish strategies.',
        'Risk managed through strategic strike and expiration choices.',
      ],
      Considerations: [
        "Crucial to understand time decay's impact and market volatility.",
        'Dependent on accurate forecasts of stock price gains.',
        'Tip: Stay alert to market trends; employ effective risk management strategies.',
      ],
    },
    other_names: 'Put Credit Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Short and long put combination balances delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Low gamma exposure due to spread.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Credit spread gains from time erosion.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short put position reduces Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short puts, low Rho sensitivity.',
  },
  {
    strategy: 'Calendar Call Spread',
    slug: 'calendar-call-spread',
    subcategory: 'Horizontal Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Involves different expiry dates, moderate capital due to time value.',
    structure:
      '- Sell a call at a higher strike (near expiration)\n- Buy a call at a higher strike (further expiration)',
    description: {
      Overview: [
        'A horizontal spread strategy involving call options with different expiration dates.',
        'Involves selling a short-term call and buying a longer-term call at the same strike.',
        'Aims to benefit from time decay in neutral market conditions.',
      ],
      Usage: [
        'Best suited for markets with low volatility and minimal price movement.',
        'Ideal for traders looking to exploit the time value difference between options.',
        'Used to generate income through premium collection.',
      ],
      'Risk Profile': [
        'Controlled risk with limited profit and loss potential.',
        'Less risky than strategies with significant market exposure.',
        'Risk managed by strategic selection of strike prices and expiration dates.',
      ],
      Considerations: [
        'Understanding of time decay and its differential impact on short and long-term options is crucial.',
        'Sensitive to changes in volatility and underlying stock movement.',
        'Key advice: Monitor for significant market events that could affect stock stability.',
      ],
    },
    other_names: 'Horizontal Call Spread\nTime Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Different expiration dates affect delta sensitivity over time.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Time decay affects gamma over different periods.',
    theta_sensitivity: 'High',
    theta_impact: 'Mixed',
    theta_rationale: 'Time decay affects options differently over time.',
    vega_sensitivity: 'High',
    vega_rationale: 'Time spread highly sensitive to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Time decay and long positions affect Rho.',
  },
  {
    strategy: 'Calendar Put Spread',
    slug: 'calendar-put-spread',
    subcategory: 'Horizontal Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Similar to calendar calls, moderate due to time value of options.',
    structure:
      '- Sell a put at a higher strike (near expiration)\n- Buy a put at a higher strike (further expiration)',
    description: {
      Overview: [
        'A neutral strategy using put options with different expiration dates.',
        'Involves selling a near-term put and buying a longer-term put at the same strike.',
        'Targets profit from time decay and minimal price movement in the underlying stock.',
      ],
      Usage: [
        'Ideal in markets with low to moderate volatility and stable prices.',
        'Suitable for traders aiming to capitalize on the time value difference between options.',
        'Utilized for income generation and exploiting time decay.',
      ],
      'Risk Profile': [
        'Limited risk with capped profit and loss potential.',
        'Lower risk compared to more aggressive market-dependent strategies.',
        'Risk managed through careful timing and choice of expiration dates.',
      ],
      Considerations: [
        'Requires understanding of how time decay differently affects short and long-term options.',
        'Influenced by near-term stock stability and longer-term market expectations.',
        'Practical tip: Stay vigilant for unexpected market movements; adjust the strategy as needed.',
      ],
    },
    other_names: 'Horizontal Put Spread\nTime Spread',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Similar to calendar call, time factor affects delta sensitivity.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Similar to calendar calls, time affects gamma.',
    theta_sensitivity: 'High',
    theta_impact: 'Mixed',
    theta_rationale: 'Similar to calendar calls; time affects differently.',
    vega_sensitivity: 'High',
    vega_rationale: 'Similar to calendar calls, high Vega risk.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long puts contribute to moderate Rho.',
  },
  {
    strategy: 'Call Broken Wing Butterfly',
    slug: 'call-broken-wing',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Involves multiple option positions, balancing cost with profits.',
    structure:
      '- Buy a call at a lower strike\n- Sell two calls at the current price\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'A variation of the butterfly spread, using call options with a skewed risk profile.',
        'Involves buying calls at lower and higher strikes, and selling more calls at a middle strike.',
        'Designed to profit from a stock remaining near the middle strike price with reduced risk.',
      ],
      Usage: [
        'Best in stable markets with low expected volatility.',
        'Suitable for traders looking for a skewed risk-reward profile in a neutral market.',
        'Utilized for earning income with limited downside risk.',
      ],
      'Risk Profile': [
        'Limited profit potential with asymmetric risk due to the unequal number of bought and sold calls.',
        'Lower risk compared to standard butterfly spreads due to the broken wing structure.',
        'Risk managed through strategic selection of strike prices and anticipating stock stability.',
      ],
      Considerations: [
        'Understanding of complex option combinations and market stability is essential.',
        'Sensitive to significant price movements and volatility.',
        'Key consideration: Precise strike selection and readiness for market shifts.',
      ],
    },
    other_names: 'Skip Strike Butterfly',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Asymmetric structure leads to moderate delta exposure.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Asymmetric structure affects gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Time decay varies due to asymmetric structure.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Asymmetric structure affects Vega.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mixed positions lead to moderate Rho.',
  },
  {
    strategy: 'Call Ratio Backspread',
    slug: 'call-ratio-backspread',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Selling fewer calls than bought; margin required but not excessive.',
    structure: '- Sell a call at a lower strike\n- Buy two calls at a higher strike',
    description: {
      Overview: [
        'A bullish and volatility-based strategy involving an unequal ratio of sold to bought call options.',
        'Entails selling one call at a lower strike and buying more calls at a higher strike.',
        "Targets scenarios expecting a significant increase in the underlying stock's price or high volatility.",
      ],
      Usage: [
        'Ideal in highly volatile markets or when a strong bullish move is anticipated.',
        'Suitable for experienced traders who can manage complex positions and significant risk.',
        'Aims for unlimited profit potential in the event of a sharp price increase.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential but with significant risk if the market moves unfavorably.',
        'High risk due to the leveraged nature of the strategy and the ratio of bought to sold calls.',
        'Risk managed through careful selection of strikes and keen market trend analysis.',
      ],
      Considerations: [
        'Requires an advanced understanding of options, market volatility, and directional trends.',
        'Highly sensitive to market movements and implied volatility changes.',
        'Important to have a clear exit strategy and the ability to make timely adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Aggressive position with variable delta depending on stock movement.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Aggressive strategy with significant gamma risk.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Long calls have varied time decay effects.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long calls increase sensitivity to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long calls contribute to moderate Rho.',
  },
  {
    strategy: 'Call Ratio Spread',
    slug: 'call-ratio-spread',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Expert',
    market_conditions: 'Income, Neutral, Bearish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Ratio of sold to bought calls; requires moderate capital.',
    structure: '- Buy a call at a lower strike\n- Sell two calls at a higher strike',
    description: {
      Overview: [
        'A bullish and volatility-based strategy involving an unequal ratio of sold to bought call options.',
        'Entails selling one call at a lower strike and buying more calls at a higher strike.',
        "Targets scenarios expecting a significant increase in the underlying stock's price or high volatility.",
      ],
      Usage: [
        'Ideal in highly volatile markets or when a strong bullish move is anticipated.',
        'Suitable for experienced traders who can manage complex positions and significant risk.',
        'Aims for unlimited profit potential in the event of a sharp price increase.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential but with significant risk if the market moves unfavorably.',
        'High risk due to the leveraged nature of the strategy and the ratio of bought to sold calls.',
        'Risk managed through careful selection of strikes and keen market trend analysis.',
      ],
      Considerations: [
        'Requires an advanced understanding of options, market volatility, and directional trends.',
        'Highly sensitive to market movements and implied volatility changes.',
        'Important to have a clear exit strategy and the ability to make timely adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Ratio of calls moderates overall delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Ratio balance affects gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Balance of short and long calls affects time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balance of long and short calls.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mix of long and short calls, moderate Rho.',
  },
  {
    strategy: 'Cash-Secured Put',
    slug: 'cash-secured-put',
    subcategory: 'Secured',
    skill_level: 'Novice',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Cash reserve needed to potentially buy the stock.',
    structure: '- Sell a put at a lower strike\n- Hold cash to buy shares if assigned',
    description: {
      Overview: [
        'A conservative option strategy for beginners involving selling put options.',
        'Requires holding enough cash to purchase the underlying stock if the option is assigned.',
        'Targets income generation through premium collection with a willingness to buy the stock at a lower price.',
      ],
      Usage: [
        "Ideal for investors who are neutral to slightly bearish on a stock and wouldn't mind owning it.",
        'Suitable for those seeking to generate income with a potential to own the underlying stock at a discount.',
        'Used as a method to acquire stocks at a predetermined lower price while earning premiums.',
      ],
      'Risk Profile': [
        'Limited profit potential to the premium received, but risk includes potentially buying the stock at the strike price.',
        'Lower risk compared to uncovered put selling due to the cash reserve.',
        'Risk managed by choosing stocks the investor is comfortable owning and setting appropriate strike prices.',
      ],
      Considerations: [
        "Critical to have a thorough understanding of the stock's fundamentals and market conditions.",
        'Influenced by stock price movements and overall market volatility.',
        'Key advice: Choose stocks wisely and be prepared for the possibility of assignment.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Directly selling puts, highly sensitive to stock price drops.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Direct put selling, high gamma at price decline.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short put position benefits from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short put position, lower Vega risk.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short puts, low Rho sensitivity.',
  },
  {
    strategy: 'Collar',
    slug: 'collar',
    subcategory: 'Protective',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Requires owning the stock, plus buying and selling options.',
    structure: '- Own the shares\n- Buy a put at a lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A protective strategy combining a covered call and a protective put.',
        'Involves holding the underlying stock, selling a call option, and buying a put option.',
        'Aims to limit downside risk while allowing for some upside potential.',
      ],
      Usage: [
        "Ideal for stock owners seeking to hedge against a decline in their stock's value.",
        'Suitable for investors looking to protect gains or limit losses in their stock positions.',
        'Used as a risk management tool in uncertain or moderately bullish markets.',
      ],
      'Risk Profile': [
        'Limited profit potential due to the sold call, but downside risk is capped by the protective put.',
        'Lower risk compared to holding the stock alone.',
        'Risk managed by balancing the strike prices and expirations of the call and put options.',
      ],
      Considerations: [
        'Important to understand the trade-offs between protection and profit potential.',
        "Influenced by the cost of the put, the premium received from the call, and the stock's performance.",
        'Practical advice: Align the strategy with your overall investment goals and market outlook.',
      ],
    },
    other_names: 'Risk-reversal\nHedge Wrapper',
    delta_sensitivity: 'Low',
    delta_rationale: 'Hedged position with reduced delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Hedged position reduces gamma exposure.',
    theta_sensitivity: 'Low',
    theta_impact: 'Mixed',
    theta_rationale: 'Combined options mitigate overall time decay impact.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Hedging reduces overall Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Offsetting positions lead to low Rho.',
  },
  {
    strategy: 'Covered Call',
    slug: 'covered-call',
    subcategory: 'Covered',
    skill_level: 'Novice',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Requires owning the stock, which is a significant capital outlay.',
    structure: '- Own the shares\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A popular strategy for beginners, combining stock ownership with selling a call option.',
        'Involves holding a long position in a stock and selling a call option on the same stock.',
        'Targets income generation through premium collection with a neutral to slightly bullish view.',
      ],
      Usage: [
        'Ideal for markets with minimal upward movement.',
        'Suitable for stock owners looking to generate additional income on their holdings.',
        'Used for downside protection and incremental income.',
      ],
      'Risk Profile': [
        'Limited profit potential with risk tied to stock ownership.',
        'Lower risk than purely bullish strategies due to premium income.',
        'Risk managed through choice of strike price and understanding stock performance.',
      ],
      Considerations: [
        'Critical to understand the trade-off between income generation and limiting upside potential.',
        "Influenced by the underlying stock's performance and market conditions.",
        'Practical advice: Choose strike prices carefully and be prepared to sell the stock if exercised.',
      ],
    },
    other_names: 'Buy-Write Strategy\n',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Stock ownership offsets some delta exposure of the short call.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Stock offsets some gamma, but still present.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short call generates income over time.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short call position limits Vega risk.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Stock position impacts Rho more than options.',
  },
  {
    strategy: 'Covered Short Straddle',
    slug: 'covered-short-straddle',
    subcategory: 'Covered',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Requires stock ownership and selling both a call and put.',
    structure:
      '- Own the shares\n- Sell a call at the current price\n- Sell a put at the current price',
    description: {
      Overview: [
        'An advanced strategy involving selling both a call and a put at the same strike while owning the underlying stock.',
        'Aims to profit from premium collection in a stable market with minimal price movement.',
        'Suitable for scenarios where significant stock movement is not expected.',
      ],
      Usage: [
        'Ideal for investors comfortable with the potential obligation to buy or sell the stock at the strike price.',
        'Best in markets with low volatility and little expected price movement.',
        'Utilized by experienced traders for income generation with a clear view on stock stability.',
      ],
      'Risk Profile': [
        'Provides limited profit potential, restricted to the premiums received.',
        'Exposes to unlimited risk on the call side if the stock price rises significantly.',
        'Risk managed through careful strike selection and readiness for potential stock assignment.',
      ],
      Considerations: [
        'Requires a deep understanding of market dynamics and options strategies.',
        "Influenced by the underlying stock's stability and market volatility.",
        'Key advice: Monitor market trends closely and be prepared for potential adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Direct exposure to underlying stock with both call and put sold.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'High exposure to gamma with both call and put.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Sold options benefit significantly from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Short positions, mixed Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options, low overall Rho impact.',
  },
  {
    strategy: 'Covered Short Strangle',
    slug: 'covered-short-strangle',
    subcategory: 'Covered',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Stock ownership and selling options; high capital for stock.',
    structure: '- Own the shares\n- Sell a put at a lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'An advanced strategy that involves selling a call and a put at different strikes while owning the underlying stock.',
        'Aims to earn premium income in a market with limited price movement.',
        'Suitable for scenarios where the stock is expected to remain within a certain price range.',
      ],
      Usage: [
        'Ideal for investors comfortable with potential stock assignment at varied strike prices.',
        'Best in stable markets with low to moderate volatility.',
        'Utilized by experienced traders for income generation and leveraging stock ownership.',
      ],
      'Risk Profile': [
        'Offers limited profit potential, mainly from the premiums received.',
        'Exposes to significant risk, especially if the stock price moves sharply in either direction.',
        'Risk managed through strategic selection of strike prices and active market monitoring.',
      ],
      Considerations: [
        'Requires thorough understanding of stock behavior and options trading.',
        "Influenced by the stock's price stability and broader market conditions.",
        'Key advice: Be prepared for stock assignment and have a clear risk management strategy.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'High exposure to stock movement with both options sold.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Similar to straddle, significant gamma risk.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Benefits from time decay in short options.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Similar to straddle, mixed Vega sensitivity.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options reduce Rho sensitivity.',
  },
  {
    strategy: 'Diagonal Call Spread',
    slug: 'diagonal-call-spread',
    subcategory: 'Horizontal Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Bullish, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Combines elements of vertical and calendar spreads.',
    structure:
      '- Buy a call at a lower strike (further expiration)\n- Sell a call at a higher strike (near expiration)',
    description: {
      Overview: [
        'A strategy combining elements of vertical and calendar spreads using call options.',
        'Involves buying a long-term call at a lower strike and selling a short-term call at a higher strike.',
        'Targets profit from both time decay and directional movement of the stock.',
      ],
      Usage: [
        'Ideal for markets with a bullish outlook and expected gradual upward movement.',
        'Suitable for traders seeking to exploit differences in time decay and strike prices.',
        'Utilized for generating income with a controlled risk profile.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, providing a balanced risk-reward dynamic.',
        'Risk is managed through the difference in expiration dates and strike prices.',
        'Less risky compared to more aggressive bullish strategies.',
      ],
      Considerations: [
        'Understanding the impact of time decay on different expiration dates is crucial.',
        "Influenced by the underlying stock's performance and market volatility.",
        'Practical tip: Adjust positions based on market movements and option expiration dates.',
      ],
    },
    other_names: "Poor Man's Covered Call",
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Combination of time and strike differences moderates delta.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Time factor and strikes lead to moderate gamma.',
    theta_sensitivity: 'High',
    theta_impact: 'Mixed',
    theta_rationale: 'Time decay impacts options with different expiries.',
    vega_sensitivity: 'High',
    vega_rationale: 'Time spread, sensitive to volatility changes.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long calls in different expirations affect Rho.',
  },
  {
    strategy: 'Diagonal Put Spread',
    slug: 'diagonal-put-spread',
    subcategory: 'Horizontal Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Bearish, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Similar to diagonal calls; moderate capital due to structure.',
    structure:
      '- Sell a put at a lower strike (near expiration)\n- Buy a put at a higher strike (further expiration)',
    description: {
      Overview: [
        'Similar to the diagonal call spread, but uses put options for a bearish outlook.',
        'Involves buying a long-term put at a higher strike and selling a short-term put at a lower strike.',
        'Aims to benefit from time decay and downward stock movement.',
      ],
      Usage: [
        'Best in markets with a bearish tilt and expected gradual downward movement.',
        'Suitable for traders looking to leverage differences in expiration dates and strike prices.',
        'Used for income generation with a controlled risk approach.',
      ],
      'Risk Profile': [
        'Provides limited profit and loss potential, offering a balanced risk-reward ratio.',
        'Risk managed through the strategic choice of different expiration dates and strike prices.',
        'Lower risk compared to aggressive bearish strategies.',
      ],
      Considerations: [
        "Requires understanding of time decay's impact on short and long-term options.",
        "Sensitive to the stock's bearish movement and market volatility.",
        'Key advice: Stay alert to market changes and adjust the strategy as needed.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Similar to diagonal calls, delta affected by time and strike.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Gamma varies with time and strike differences.',
    theta_sensitivity: 'High',
    theta_impact: 'Mixed',
    theta_rationale: 'Similar to diagonal calls; time decay varies.',
    vega_sensitivity: 'High',
    vega_rationale: 'High Vega due to difference in expirations.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long puts in spread contribute to Rho.',
  },
  {
    strategy: 'Double Diagonal',
    slug: 'double-diagonal',
    subcategory: 'Horizontal Spread',
    skill_level: 'Expert',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Involves two diagonal spreads; requires a balance of capital.',
    structure:
      '- Sell a put at a lower strike (near expiration)\n- Buy a put at an even lower strike (further expiration)\n- Sell a call at a higher strike (near expiration)\n- Buy a call at an even higer strike (further expiration)',
    description: {
      Overview: [
        'A complex strategy that combines diagonal call and put spreads.',
        'Involves buying long-term calls and puts at different strikes and selling short-term calls and puts also at different strikes.',
        'Targets profit from time decay and minimal movement in the underlying stock.',
      ],
      Usage: [
        'Ideal for markets with low volatility and little expected price movement.',
        'Suitable for experienced traders skilled in managing multiple positions and expirations.',
        'Utilized for capitalizing on time decay in a range-bound market.',
      ],
      'Risk Profile': [
        'Offers limited profit potential with controlled risk.',
        'Risk managed through careful selection of strikes and expirations, balancing call and put positions.',
        'Complex risk dynamics require active monitoring and potential adjustments.',
      ],
      Considerations: [
        'Requires a deep understanding of options strategies and market behavior.',
        'Influenced by the stability of the underlying stock and overall market conditions.',
        'Practical advice: Be prepared for active management and adjustments based on market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Complex structure with balanced delta sensitivity.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Complex structure with varied gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Mixed',
    theta_rationale: 'Complex structure with varied time decay effects.',
    vega_sensitivity: 'High',
    vega_rationale: 'Complex spread, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mix of options leads to moderate Rho.',
  },
  {
    strategy: 'Guts Spread',
    slug: 'guts',
    subcategory: 'Bi-Directional',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Involves ITM options; higher premium costs.',
    structure: '- Buy a call at a lower strike\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A high-risk, high-reward strategy involving the simultaneous buying of in-the-money call and put options.',
        'Aims to profit from significant price movements in either direction.',
        'Suitable for scenarios where large stock price swings are anticipated.',
      ],
      Usage: [
        'Best in highly volatile markets where substantial price movements are expected.',
        'Suitable for experienced traders who are comfortable with complex, high-stakes positions.',
        'Utilized for capturing significant profits from large market movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with the risk limited to the premiums paid.',
        'High risk due to the cost of buying in-the-money options.',
        'Risk managed through careful market analysis and timing.',
      ],
      Considerations: [
        'Requires a deep understanding of market dynamics and option pricing.',
        'Sensitive to market volatility and direction.',
        'Key advice: Monitor market indicators closely and be prepared for swift adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'ITM options have higher delta sensitivity.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'ITM positions increase gamma sensitivity.',
    theta_sensitivity: 'Low',
    theta_impact: 'Negative',
    theta_rationale: 'ITM options less affected by time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'ITM options have increased Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'ITM options have moderate Rho sensitivity.',
  },
  {
    strategy: 'Inverse Call Broken Wing Butterfly',
    slug: 'inverse-call-broken-wing',
    subcategory: 'Bi-Directional',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Variation of butterfly spread; structured to use moderate capital.',
    structure:
      '- Buy two calls at a lower strike\n- Sell a call at an even lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A variation of the butterfly spread, using call options, but with an inverted risk profile.',
        'Involves selling calls at lower and higher strikes, and buying more calls at a middle strike.',
        'Designed for neutral markets, aiming to profit from minimal stock movement with a different risk distribution.',
      ],
      Usage: [
        'Ideal in stable markets with low expected volatility.',
        'Suitable for traders seeking a skewed risk-reward profile in a neutral market.',
        'Utilized for earning income with a different risk approach compared to traditional butterfly spreads.',
      ],
      'Risk Profile': [
        'Limited profit potential with asymmetric risk due to the unequal number of sold and bought calls.',
        'Lower risk compared to standard butterfly spreads due to the inverse structure.',
        'Risk managed through strategic selection of strike prices and anticipating stock stability.',
      ],
      Considerations: [
        'Understanding of complex option combinations and market stability is essential.',
        'Sensitive to significant price movements and volatility.',
        'Key consideration: Precise strike selection and readiness for market shifts.',
      ],
    },
    other_names: 'Inverse Skip Strike Butterfly',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Modified butterfly spread, balanced but with directional bias.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Modified butterfly with specific gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Asymmetric spread leads to varied time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Asymmetric, but balanced Vega exposure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Asymmetric spread with moderate Rho.',
  },
  {
    strategy: 'Inverse Iron Butterfly',
    slug: 'inverse-iron-butterfly',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Similar to iron butterfly but with potentially more risk.',
    structure:
      '- Sell a put at a lower strike\n- Buy a put at a higher strike\n- Buy a call at a higher strike\n- Sell a call at an even higher strike',
    description: {
      Overview: [
        'A complex strategy that combines elements of the straddle and strangle, using both calls and puts.',
        'Involves selling at-the-money call and put options and buying out-of-the-money call and put options.',
        'Aims to capitalize on significant price movements in any direction.',
      ],
      Usage: [
        'Best in markets where extreme volatility and significant price swings are expected.',
        'Suitable for experienced traders skilled in managing complex positions.',
        'Utilized for capturing profits from large market movements in either direction.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with risk limited to the distance between the strikes minus the net premium received.',
        'High risk due to the potential for significant market moves.',
        'Risk managed through strategic selection of strike prices and expirations.',
      ],
      Considerations: [
        'Requires advanced understanding of options trading and market dynamics.',
        'Highly sensitive to market volatility and stock price movements.',
        'Practical advice: Stay vigilant and be prepared for swift adjustments based on market changes.',
      ],
    },
    other_names: 'Long Iron Butterfly',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Combination of options reduces delta sensitivity.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Combination of options balances gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Combination of options with different time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Combination of options creates mixed Vega.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Balanced positions lead to moderate Rho.',
  },
  {
    strategy: 'Inverse Iron Condor',
    slug: 'inverse-iron-condor',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Modified iron condor; requires balance in capital for various options.',
    structure:
      '- Buy a put at a lower strike\n- Sell a put at an even lower strike\n- Buy a call at a higher strike\n- Sell a call at an even higher strike',
    description: {
      Overview: [
        'A variation of the iron condor strategy, designed to profit from significant price movements.',
        'Involves buying out-of-the-money call and put options and selling in-the-money call and put options.',
        'Targets scenarios expecting large stock price swings, but within a certain range.',
      ],
      Usage: [
        'Ideal in highly volatile markets with expectations of significant price movements.',
        'Suitable for traders skilled in balancing complex positions and managing risk.',
        'Utilized for capitalizing on large price movements within defined limits.',
      ],
      'Risk Profile': [
        'Limited profit potential, with risk confined to the premiums paid for the options.',
        'Moderate risk compared to more aggressive strategies.',
        'Risk managed through careful selection of strike prices and monitoring market trends.',
      ],
      Considerations: [
        'Requires a thorough understanding of options strategies and market behavior.',
        'Sensitive to market volatility and the ability to stay within the price range.',
        'Key advice: Be prepared for active management and timely adjustments.',
      ],
    },
    other_names: 'Long Iron Condor',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Structured to have a balanced but directional delta exposure.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Varied positions create specific gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Varied time decay effects due to option structure.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Varied positions, moderate Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mix of options positions, moderate Rho.',
  },
  {
    strategy: 'Inverse Put Broken Wing Butterfly',
    slug: 'inverse-put-broken-wing',
    subcategory: 'Bi-Directional',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Asymmetric position; moderate capital due to option costs.',
    structure:
      '- Sell a put at a lower strike\n- Buy two puts at a higher strike\n- Sell a put at an even higher strike',
    description: {
      Overview: [
        'A variation of the butterfly spread, utilizing put options with an inverted risk profile.',
        'Involves selling puts at lower and higher strikes, and buying more puts at a middle strike.',
        'Designed for markets with minimal movement, aiming for profit with an asymmetric risk distribution.',
      ],
      Usage: [
        'Ideal in stable markets with low expected volatility.',
        'Suitable for traders seeking a unique risk-reward profile in a neutral setting.',
        'Utilized for income generation with a modified risk approach compared to traditional butterfly spreads.',
      ],
      'Risk Profile': [
        'Limited profit potential with asymmetric risk due to the unequal number of sold and bought puts.',
        'Comparatively lower risk than standard butterfly spreads due to the inverse structure.',
        'Risk managed through careful strike selection and anticipating market stability.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market dynamics.',
        'Sensitive to significant price movements and volatility.',
        'Key consideration: Accurate strike selection and readiness for market shifts.',
      ],
    },
    other_names: 'Inverse Skip Strike Butterfly',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Asymmetric put strategy, moderate delta sensitivity.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Asymmetric put structure affects gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Asymmetric structure affects time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balanced Vega exposure in asymmetric structure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Asymmetric structure with moderate Rho.',
  },
  {
    strategy: 'Iron Butterfly',
    slug: 'iron-butterfly',
    subcategory: 'Butterfly Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Combination of spreads; balances bought and sold options.',
    structure:
      '- Buy a put at a lower strike\n- Sell a put at the current price\n- Sell a call at the current price\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'A non-directional, income-generating strategy that combines a short straddle and a long strangle.',
        'Involves selling an at-the-money call and put, and buying an out-of-the-money call and put for protection.',
        'Targets a specific price range where the stock is expected to remain at expiration.',
      ],
      Usage: [
        'Best in stable markets with little expected volatility.',
        'Suitable for traders seeking income through premium collection with defined risk.',
        'Aims to capitalize on minimal stock movement within a narrow price range.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk than aggressive or directional strategies.',
        'Risk management focuses on accurate price target predictions and controlling maximum loss.',
      ],
      Considerations: [
        'Requires an understanding of complex option combinations and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key advice: Select strikes carefully and monitor for unexpected market shifts.',
      ],
    },
    other_names: 'Short Iron Butterfly',
    delta_sensitivity: 'Low',
    delta_rationale: 'Balanced position with limited delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced position limits gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short options in center gain from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Short options in center balance Vega.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options limit Rho sensitivity.',
  },
  {
    strategy: 'Iron Condor',
    slug: 'iron-condor',
    subcategory: 'Condor Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Involves four options but balanced between buys and sells.',
    structure:
      '- Sell a put at a lower strike\n- Buy a put at an even lower strike\n- Sell a call at a higher strike\n- Buy a call at an even higher strike',
    description: {
      Overview: [
        'A market-neutral strategy combining two vertical spreads – a bull put spread and a bear call spread.',
        'Involves selling an out-of-the-money put and call, and buying a further out-of-the-money put and call for protection.',
        'Aims to profit from minimal stock movement, capturing premium within a defined range.',
      ],
      Usage: [
        'Ideal in stable markets with limited expected volatility.',
        'Suitable for traders seeking to profit from a range-bound stock while controlling risk.',
        'Utilized for earning income with limited downside risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk compared to strategies with larger directional bets.',
        'Risk management includes balancing the strike prices and expiration dates of the spreads.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key consideration: Precise strike selection and vigilance on price movements.',
      ],
    },
    other_names: 'Short Iron Condor\nCondor Spread',
    delta_sensitivity: 'Low',
    delta_rationale: 'Range-bound strategy, low delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Range-bound, limited gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short positions benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balanced positions, moderate Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short positions in condor reduce Rho impact.',
  },
  // {
  //   strategy: 'Iron Corridor',
  //   slug: '',
  //   subcategory: 'Neutral',
  //   skill_level: 'Intermediate',
  //   market_conditions: 'Neutral',
  //   risk: 'Moderate',
  //   profit: 'Capped',
  //   loss: 'Capped',
  //   capital_requirement: 'Moderate',
  //   capital_required_rationale: 'Similar to iron condor; balances outlays and credits.',
  //   structure:
  //     '- Sell a put at strike A\n- Sell a call at strike A\n- Buy a put at strike B\n- Buy a call at strike B',
  //   description: {
  //     Overview: [
  //       'A multi-leg, bearish strategy designed for experienced traders.',
  //       'Involves selling one call at a lower strike, buying one at a higher strike, and selling another call at an even higher strike.',
  //       'Targets scenarios where the stock price is expected to drop or remain stagnant.',
  //     ],
  //     Usage: [
  //       'Best employed in markets with bearish or neutral trends.',
  //       'Suited for traders seeking to capitalize on downward stock movements while limiting risk.',
  //       'Aims to generate income while managing risk through a structured approach.',
  //     ],
  //     'Risk Profile': [
  //       'Offers a favorable risk-reward balance with limited loss potential and unlimited profit potential.',
  //       'Less risky compared to single-leg bearish strategies.',
  //       'Risk management includes strategic strike selection and monitoring of stock movement.',
  //     ],
  //     Considerations: [
  //       'Requires understanding of time decay and options liquidity.',
  //       'Key factors include stock price direction and volatility.',
  //       'Advice: Be cautious of unexpected bullish trends; know when to adjust or exit the strategy.',
  //     ],
  //   },
  //   other_names: '',
  //   delta_sensitivity: 'Low',
  //   delta_rationale: 'Similar to iron condor, designed for low delta exposure.',
  //   gamma_sensitivity: 'Low',
  //   gamma_rationale: 'Similar to iron condor, low gamma sensitivity.',
  //   theta_sensitivity: 'High',
  //   theta_impact: 'Positive',
  //   theta_rationale: 'Similar to iron condor; gains from time decay.',
  //   vega_sensitivity: 'Moderate',
  //   vega_rationale: 'Similar to iron condor, moderate Vega.',
  //   rho_sensitivity: 'Low',
  //   rho_rationale: 'Similar to iron condor, low Rho sensitivity.',
  // },
  {
    strategy: 'Jade Lizard',
    slug: 'jade-lizard',
    subcategory: 'Combination',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Bullish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Sells a put and a call spread; requires capital for risk management.',
    structure:
      '- Sell a put at a lower strike\n- Sell a call at a higher strike\n- Buy a call at an even higher strike',
    description: {
      Overview: [
        'A premium collection strategy that combines a short put with a short call spread.',
        'Involves selling an out-of-the-money put and a call spread (selling a call and buying a higher strike call).',
        'Designed to capture premium without taking on downside risk.',
      ],
      Usage: [
        'Ideal in markets with neutral to slightly bullish outlooks, where the stock price is not expected to drop significantly.',
        'Suitable for experienced traders comfortable with managing complex positions.',
        'Utilized for income generation with a skewed risk-reward balance.',
      ],
      'Risk Profile': [
        'Offers limited profit potential from premiums, with significant risk if the stock rises sharply.',
        'Risk is managed by not overlapping the put strike and the call spread, creating no downside risk.',
        'Active position management is crucial for mitigating risk.',
      ],
      Considerations: [
        'Requires understanding of options strategy mechanics and market sentiment.',
        'Influenced by stock price movement and overall market volatility.',
        'Key advice: Be vigilant about market trends and ready to adjust positions as needed.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Combination of options provides a balanced delta exposure.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Combination of options for specific gamma.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short options strategy benefits from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Mix of options creates specific Vega exposure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options dominate, limiting Rho.',
  },
  {
    strategy: 'Long Call',
    slug: 'long-call',
    subcategory: 'Single-Leg',
    skill_level: 'Novice',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'Moderate',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Buying calls, especially ATM/ITM, can be capital intensive.',
    structure: '- Buy a call at a lower strike',
    description: {
      Overview: [
        'A basic options strategy involving the purchase of call options.',
        'Suitable for scenarios where a significant increase in the stock price is anticipated.',
        'Investors buy calls hoping the stock will rise above the strike price before expiration.',
      ],
      Usage: [
        'Ideal for bullish investors who expect a stock to rise substantially.',
        'Suitable for traders with a high-risk tolerance and a clear bullish outlook.',
        'Utilized as a leveraged way to profit from stock price increases.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with risk limited to the premium paid.',
        'Less capital-intensive compared to buying the stock outright.',
        'Risk managed by selecting appropriate strike prices and expiration dates.',
      ],
      Considerations: [
        "Important to understand the stock's potential for growth and market conditions.",
        'Influenced by stock price volatility and market trends.',
        'Practical advice: Monitor the underlying stock closely and be aware of time decay.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: "Direct exposure to underlying's price increase.",
    gamma_sensitivity: 'High',
    gamma_rationale: "Direct sensitivity to underlying's gamma.",
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Loses value with time due to theta decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Direct sensitivity to volatility increase.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Direct exposure to interest rate changes.',
  },
  {
    strategy: 'Long Call Butterfly',
    slug: 'long-call-butterfly',
    subcategory: 'Butterfly Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Balanced strategy with both bought and sold calls.',
    structure:
      '- Buy a call at a lower strike\n- Sell two calls at the current price\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'A market-neutral strategy that involves buying and selling call options at three different strike prices.',
        'Combines buying one in-the-money call, selling two at-the-money calls, and buying one out-of-the-money call.',
        'Targets a specific price range where the stock is expected to remain at expiration.',
      ],
      Usage: [
        'Best in stable markets with little expected volatility.',
        'Suitable for traders seeking to profit from a stock remaining within a narrow price range.',
        'Utilized for income generation with defined risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk compared to aggressive or directional strategies.',
        'Risk management focuses on accurate price target predictions and time decay.',
      ],
      Considerations: [
        'Requires understanding of complex option combinations and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key advice: Choose strike prices carefully and monitor for unexpected market shifts.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Balanced strategy limits delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced strategy limits gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Balanced structure limits overall time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balanced structure limits Vega risk.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long positions increase Rho sensitivity.',
  },
  {
    strategy: 'Long Call Condor',
    slug: 'long-call-condor',
    subcategory: 'Condor Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Similar to butterfly but with wider strike ranges.',
    structure:
      '- Sell a call at a lower strike\n- Buy a call at an even lower strike\n- Sell a call at a higher strike\n- Buy a call at an even higher strike',
    description: {
      Overview: [
        'A neutral strategy similar to the butterfly spread but with a wider range of profitability.',
        'Involves buying one lower strike call, selling one lower middle strike call, selling one higher middle strike call, and buying one higher strike call.',
        'Aims to profit from the stock price staying within a wider range compared to a butterfly.',
      ],
      Usage: [
        'Ideal in stable markets where significant price movement is not expected.',
        'Suitable for traders looking to profit from limited stock movement within a broader range than a butterfly spread.',
        'Utilized for generating income with a controlled risk profile.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a more flexible risk environment compared to a butterfly.',
        'Risk managed by balancing the strike prices and expiration dates of the calls.',
        'Lower risk compared to more aggressive directional strategies.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market behavior.',
        'Influenced by the stability of the underlying stock and overall market conditions.',
        'Key advice: Be prepared for active management and adjustments based on market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Structured for limited delta sensitivity in a range.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Limited gamma within a range.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Structured to reduce overall time decay impact.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Limited Vega exposure in structure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Structured positions with moderate Rho.',
  },
  {
    strategy: 'Long Combo',
    slug: 'long-combo',
    subcategory: 'Synthetic',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Synthetic strategy; requires capital for both call and put.',
    structure: '- Sell a put at a lower strike\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'A synthetic strategy that mimics owning the underlying stock, involving buying a call and selling a put at the same strike.',
        'Aims to profit from a significant upward movement in the stock price.',
        'Suitable for scenarios where a strong bullish trend is anticipated.',
      ],
      Usage: [
        'Ideal for investors with a very bullish outlook on a stock and seeking a stock-like position without actually owning it.',
        'Suitable for traders comfortable with the risks associated with both long calls and short puts.',
        'Utilized for capitalizing on substantial upward stock movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential similar to owning the stock, but also carries unlimited downside risk.',
        'Risk is managed by selecting appropriate strike prices and being prepared for potential stock price declines.',
        'Requires active monitoring and a clear understanding of market trends.',
      ],
      Considerations: [
        "Important to have a thorough understanding of the stock's potential for growth and overall market conditions.",
        'Influenced by stock price volatility and broader market trends.',
        'Practical advice: Be prepared for the obligations of the short put position and the need for potential adjustments.',
      ],
    },
    other_names: 'Synthetic Long Stock',
    delta_sensitivity: 'High',
    delta_rationale: 'Synthetic strategy, high delta exposure mimicking stock.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Mimics stock, high gamma sensitivity.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Negative',
    theta_rationale: 'Mimics stock movement but affected by time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Synthetic position, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Synthetic stock position, moderate Rho.',
  },
  {
    strategy: 'Long Put',
    slug: 'long-put',
    subcategory: 'Single-Leg',
    skill_level: 'Novice',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'Moderate',
    profit: 'Nearly Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Buying puts, particularly ATM/ITM, requires higher capital.',
    structure: '- Buy a put at a lower strike',
    description: {
      Overview: [
        'A basic options strategy involving the purchase of put options.',
        'Suitable for scenarios where a significant decrease in the stock price is anticipated.',
        'Investors buy puts hoping the stock will fall below the strike price before expiration.',
      ],
      Usage: [
        'Ideal for bearish investors who expect a stock to decline substantially.',
        'Suitable for traders with a high-risk tolerance and a clear bearish outlook.',
        'Utilized as a leveraged way to profit from stock price decreases.',
      ],
      'Risk Profile': [
        'Offers a limited profit potential with risk confined to the premium paid.',
        'Less capital-intensive compared to short selling the stock.',
        'Risk managed by selecting appropriate strike prices and expiration dates.',
      ],
      Considerations: [
        "Important to understand the stock's potential for decline and market conditions.",
        'Influenced by stock price volatility and market trends.',
        'Practical advice: Monitor the underlying stock closely and be aware of time decay.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Direct sensitivity to price decrease of the underlying.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Direct sensitivity to gamma in declining market.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Time decay erodes value of long puts.',
    vega_sensitivity: 'High',
    vega_rationale: 'Sensitive to increase in volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long puts increase sensitivity to Rho.',
  },
  {
    strategy: 'Long Put Butterfly',
    slug: 'long-put-butterfly',
    subcategory: 'Butterfly Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Combination of long and short puts; balanced capital usage.',
    structure:
      '- Buy a put at a lower strike\n- Sell two puts at the current price\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A market-neutral strategy that involves buying and selling put options at three different strike prices.',
        'Combines buying one in-the-money put, selling two at-the-money puts, and buying one out-of-the-money put.',
        'Targets a specific price range where the stock is expected to remain at expiration.',
      ],
      Usage: [
        'Best in stable markets with little expected volatility.',
        'Suitable for traders seeking to profit from a stock remaining within a narrow price range.',
        'Utilized for income generation with defined risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk compared to aggressive or directional strategies.',
        'Risk management focuses on accurate price target predictions and time decay.',
      ],
      Considerations: [
        'Requires understanding of complex option combinations and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key advice: Choose strike prices carefully and monitor for unexpected market shifts.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Balanced strategy with limited delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced strategy with limited gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Balanced strategy with limited time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balanced structure, moderate Vega risk.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long positions affect Rho sensitivity.',
  },
  {
    strategy: 'Long Put Condor',
    slug: 'long-put-condor',
    subcategory: 'Condor Spread',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Like put butterfly but with wider strikes; moderate capital.',
    structure:
      '- Sell a put at a lower strike\n- Buy a put at an even lower strike\n- Sell a put at a higher strike\n- Buy a put at an even higher strike',
    description: {
      Overview: [
        'A neutral strategy similar to the butterfly spread but with a wider range of profitability.',
        'Involves buying one lower strike put, selling one lower middle strike put, selling one higher middle strike put, and buying one higher strike put.',
        'Aims to profit from the stock price staying within a wider range compared to a butterfly.',
      ],
      Usage: [
        'Ideal in stable markets where significant price movement is not expected.',
        'Suitable for traders looking to profit from limited stock movement within a broader range than a butterfly spread.',
        'Utilized for generating income with a controlled risk profile.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a more flexible risk environment compared to a butterfly.',
        'Risk managed by balancing the strike prices and expiration dates of the puts.',
        'Lower risk compared to more aggressive directional strategies.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market behavior.',
        'Influenced by the stability of the underlying stock and overall market conditions.',
        'Key advice: Be prepared for active management and adjustments based on market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Limited delta exposure within a certain price range.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Limited gamma exposure within a range.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Structured to have reduced impact from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Structured to limit Vega exposure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Spread structure leads to moderate Rho.',
  },
  {
    strategy: 'Long Synthetic Future',
    slug: 'long-synthetic-future',
    subcategory: 'Synthetic',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale:
      'Involves buying a call and selling a put; significant capital needed.',
    structure: '- Sell a put at the current price\n- Buy a call at the current price',
    description: {
      Overview: [
        'A strategy that simulates a long future position using options, involving buying a call and selling a put at the same strike and expiration.',
        'Designed to mimic the payoff of a future contract with the flexibility of options.',
        'Suitable for scenarios where significant upward movement in the underlying asset is expected.',
      ],
      Usage: [
        'Ideal for investors bullish on a stock or commodity and seeking a future-like position without actually trading futures.',
        'Suitable for traders comfortable with the risks of both long calls and short puts.',
        'Utilized for capitalizing on substantial upward movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential similar to owning a future, but also carries unlimited downside risk.',
        'Risk managed by selecting appropriate strike prices and being prepared for potential price declines.',
        'Requires active monitoring and understanding of market trends.',
      ],
      Considerations: [
        "Important to have a thorough grasp of market dynamics and the underlying asset's potential.",
        "Influenced by the asset's price volatility and broader market trends.",
        'Practical advice: Stay vigilant about market trends and be ready for potential adjustments.',
      ],
    },
    other_names: 'Synthetic Long Future',
    delta_sensitivity: 'High',
    delta_rationale: 'Mimics underlying stock, high delta sensitivity.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Mimics underlying, high gamma sensitivity.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Mimics stock but sensitive to time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Mimics stock, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mimics underlying, moderate Rho sensitivity.',
  },
  {
    strategy: 'Protective Put',
    slug: 'protective-put',
    subcategory: 'Protective',
    skill_level: 'Novice',
    market_conditions: 'Growth, Bullish, Stable',
    risk: 'Moderate',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Stock ownership plus buying puts adds to capital requirement.',
    structure: '- Own the shares\n- Buy a put at a lower strike',
    description: {
      Overview: [
        'A hedging strategy that involves owning the underlying stock and buying a put option for protection.',
        'Aims to safeguard against significant downward movement in the stock while retaining upside potential.',
        'Suitable for stock owners seeking downside protection.',
      ],
      Usage: [
        'Ideal for investors who are bullish on a stock but concerned about potential short-term downside risks.',
        'Suitable for traders looking to protect unrealized gains or minimize potential losses.',
        'Utilized as an insurance policy against stock depreciation.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with downside risk limited to the premium paid for the put.',
        'Risk is managed by the protective put, which provides a safety net in case of stock price decline.',
        'Balances bullish position with downside protection.',
      ],
      Considerations: [
        'Important to select an appropriate strike price and expiration date for the put.',
        "Influenced by the cost of the put option and the stock's performance.",
        'Practical advice: Weigh the cost of protection against the potential risks of stock ownership.',
      ],
    },
    other_names: 'Married Put',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Stock ownership with a put hedge, moderate delta sensitivity.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Stock with put hedge creates moderate gamma.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Long put decays over time, reducing protection value.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Long put increases, but stock offsets Vega.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Combined stock and put positions affect Rho.',
  },
  {
    strategy: 'Put Broken Wing Butterfly',
    slug: 'put-broken-wing',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Asymmetric put strategy; moderate capital due to spread structure.',
    structure:
      '- Buy a put at a lower strike\n- Sell two puts at the current price\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A variation of the butterfly spread using put options, but with an unequal wing width.',
        'Involves buying puts at lower and higher strikes, and selling more puts at a middle strike.',
        'Designed for neutral markets, aiming to profit from minimal stock movement with a different risk distribution.',
      ],
      Usage: [
        'Ideal in stable markets with low expected volatility.',
        'Suitable for traders seeking a unique risk-reward profile in a neutral market setting.',
        'Utilized for earning income with a modified risk approach compared to traditional butterfly spreads.',
      ],
      'Risk Profile': [
        'Limited profit potential with asymmetric risk due to the unequal number of sold and bought puts.',
        'Comparatively lower risk than standard butterfly spreads due to the broken wing structure.',
        'Risk managed through strategic selection of strike prices and anticipating market stability.',
      ],
      Considerations: [
        'Requires understanding of complex option combinations and market dynamics.',
        'Sensitive to significant price movements and volatility.',
        'Key consideration: Accurate strike selection and readiness for market shifts.',
      ],
    },
    other_names: 'Skip Strike Butterfly',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Asymmetric structure leads to moderate delta exposure.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Asymmetric put strategy, specific gamma.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Asymmetric structure leads to varied time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Asymmetric structure, specific Vega exposure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Mixed put positions lead to moderate Rho.',
  },
  {
    strategy: 'Put Ratio Backspread',
    slug: 'put-ratio-backspread',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Nearly Unlimited',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'More puts bought than sold; requires margin but not excessive.',
    structure: '- Buy two puts at a lower strike\n- Sell a put at a higher strike',
    description: {
      Overview: [
        'A bearish and volatility-based strategy involving an unequal ratio of sold to bought put options.',
        'Entails selling one put at a lower strike and buying more puts at a higher strike.',
        "Targets scenarios expecting a significant decrease in the underlying stock's price or high volatility.",
      ],
      Usage: [
        'Ideal in highly volatile markets or when a strong bearish move is anticipated.',
        'Suitable for experienced traders who can manage complex positions and significant risk.',
        'Aims for unlimited profit potential in the event of a sharp price decrease.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential but with significant risk if the market moves unfavorably.',
        'High risk due to the leveraged nature of the strategy and the ratio of bought to sold puts.',
        'Risk managed through careful selection of strikes and keen market trend analysis.',
      ],
      Considerations: [
        'Requires an advanced understanding of options, market volatility, and directional trends.',
        'Highly sensitive to market movements and implied volatility changes.',
        'Important to have a clear exit strategy and the ability to make timely adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Aggressive position with variable delta depending on stock movement.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Aggressive position with high gamma risk.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Long puts have varied effects from time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long puts, high sensitivity to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long put positions increase Rho sensitivity.',
  },
  {
    strategy: 'Put Ratio Spread',
    slug: 'put-ratio-spread',
    subcategory: 'Asymmetric Spread',
    skill_level: 'Expert',
    market_conditions: 'Income, Neutral, Bullish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Involves selling and buying puts; balanced but needs margin.',
    structure: '- Sell two puts at a lower strike\n- Buy a put at a higher strike',
    description: {
      Overview: [
        'A strategy involving buying and selling puts in a particular ratio.',
        'Typically includes selling more put options at a lower strike price and buying fewer puts at a higher strike.',
        'Aims to profit from either stable or slightly declining stock prices.',
      ],
      Usage: [
        'Ideal in markets where a moderate decline or stability in the stock price is anticipated.',
        'Suitable for traders who are moderately bearish and can manage complex positions.',
        'Utilized for generating income with a potential for unlimited loss if the stock declines significantly.',
      ],
      'Risk Profile': [
        'Limited profit potential if the stock stays stable or declines moderately.',
        'Carries significant risk if the stock price drops sharply, as losses can be substantial.',
        'Risk managed by carefully selecting strike prices and maintaining a favorable ratio.',
      ],
      Considerations: [
        'Requires an understanding of market trends and the potential for downside movements.',
        'Influenced by stock price volatility and broader market conditions.',
        'Practical advice: Monitor the underlying stock closely and be prepared for adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Ratio of puts moderates overall delta exposure.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Put ratios balance gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Mixed',
    theta_rationale: 'Balance of short and long puts affects time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Balance of long and short puts affects Vega.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Balance of long and short puts affects Rho.',
  },
  {
    strategy: 'Reverse Jade Lizard',
    slug: 'reverse-jade-lizard',
    subcategory: 'Combination',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Bearish, Stable',
    risk: 'Moderate',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Variation of jade lizard; moderate capital for risk balance.',
    structure:
      '- Sell a put at a lower strike\n- Buy a put at an even lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A premium collection strategy that combines a short put with a short call spread.',
        'Involves selling an out-of-the-money put and a call spread (selling a call and buying a higher strike call).',
        'Designed to capture premium without taking on downside risk.',
      ],
      Usage: [
        'Ideal in markets with neutral to slightly bullish outlooks, where the stock price is not expected to drop significantly.',
        'Suitable for experienced traders comfortable with managing complex positions.',
        'Utilized for income generation with a skewed risk-reward balance.',
      ],
      'Risk Profile': [
        'Offers limited profit potential from premiums, with significant risk if the stock rises sharply.',
        'Risk is managed by not overlapping the put strike and the call spread, creating no downside risk.',
        'Active position management is crucial for mitigating risk.',
      ],
      Considerations: [
        'Requires understanding of options strategy mechanics and market sentiment.',
        'Influenced by stock price movement and overall market volatility.',
        'Key advice: Be vigilant about market trends and ready to adjust positions as needed.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Structured for a specific directional bias in delta.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Structured for specific directional gamma.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short options benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Combination of options for specific Vega.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options reduce overall Rho impact.',
  },
  {
    strategy: 'Short Call',
    slug: 'short-call',
    subcategory: 'Naked',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Bearish, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Potential for unlimited losses; high margin requirement.',
    structure: '- Sell a call at a higher strike',
    description: {
      Overview: [
        'A bearish strategy involving selling a call option.',
        'Suitable for scenarios where a decrease or minimal increase in the stock price is anticipated.',
        'Traders sell calls hoping the stock will remain below the strike price or decline.',
      ],
      Usage: [
        'Ideal for bearish or neutral investors who do not expect a stock to rise significantly.',
        'Suitable for traders with a high-risk tolerance and a clear bearish or neutral outlook.',
        'Utilized as a way to generate income through premium collection.',
      ],
      'Risk Profile': [
        'Limited profit potential to the premium received, but carries unlimited downside risk.',
        'High risk, especially if the stock price rises significantly above the strike price.',
        'Risk managed by selecting appropriate strike prices and expiration dates.',
      ],
      Considerations: [
        "Important to understand the stock's potential for growth and market conditions.",
        'Influenced by stock price volatility and market trends.',
        'Practical advice: Be prepared for the obligation to sell the stock if it exceeds the strike price.',
      ],
    },
    other_names: 'Naked Call',
    delta_sensitivity: 'High',
    delta_rationale: "Direct exposure to stock's price increase, high delta risk.",
    gamma_sensitivity: 'High',
    gamma_rationale: 'Direct exposure to gamma with short call.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short position benefits from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short position benefits from Vega decrease.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short position, low sensitivity to Rho.',
  },
  {
    strategy: 'Short Call Butterfly',
    slug: 'short-call-butterfly',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Sold and bought calls balance out the capital requirement.',
    structure:
      '- Sell a call at a lower strike\n- Buy two calls at the current price\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A market-neutral strategy involving selling and buying call options at three different strike prices.',
        'Combines selling one in-the-money call, buying two at-the-money calls, and selling one out-of-the-money call.',
        'Targets a specific price range where the stock is expected to remain at expiration.',
      ],
      Usage: [
        'Best in stable markets with little expected volatility.',
        'Suitable for traders seeking to profit from a stock remaining within a narrow price range.',
        'Utilized for income generation with defined risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk compared to aggressive or directional strategies.',
        'Risk management focuses on accurate price target predictions and time decay.',
      ],
      Considerations: [
        'Requires understanding of complex option combinations and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key advice: Choose strike prices carefully and monitor for unexpected market shifts.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Balanced position with limited delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced position, limited gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short center calls benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Short center calls reduce Vega risk.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options in strategy reduce Rho.',
  },
  {
    strategy: 'Short Call Condor',
    slug: 'short-call-condor',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Combination of short and long calls; balanced capital usage.',
    structure:
      '- Buy a call at a lower strike\n- Sell a call at an even lower strike\n- Buy a call at a higher price\n- Sell a call at an even higher strike',
    description: {
      Overview: [
        'A neutral strategy similar to the call butterfly but with a wider profit range.',
        'Involves selling one lower strike call, buying one lower middle strike call, buying one higher middle strike call, and selling one higher strike call.',
        'Aims to profit from the stock price staying within a broader range compared to a butterfly spread.',
      ],
      Usage: [
        'Ideal in stable markets where significant price movement is not expected.',
        'Suitable for traders seeking profit from limited stock movement within a wider range.',
        'Utilized for generating income with controlled risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a more flexible risk environment compared to a butterfly spread.',
        'Risk managed by balancing the strike prices and expiration dates of the calls.',
        'Lower risk compared to more aggressive directional strategies.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market behavior.',
        'Influenced by the stability of the underlying stock and overall market conditions.',
        'Key advice: Be prepared for active management and adjustments based on market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Structured for limited delta sensitivity in a range.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Limited gamma sensitivity in a range.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short call positions benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Limited Vega exposure due to structure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Predominantly short options, low Rho.',
  },
  {
    strategy: 'Short Combo',
    slug: 'short-combo',
    subcategory: 'Synthetic',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Nearly Unlimited',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale:
      'Short a call and long a put; significant margin for the short call.',
    structure: '- Buy a put at a lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A bearish strategy that mimics a short future position using options, involving selling a call and buying a put at the same strike and expiration.',
        'Designed to profit from a significant downward movement in the stock price.',
        'Suitable for scenarios where a strong bearish trend is anticipated.',
      ],
      Usage: [
        'Ideal for investors bearish on a stock or commodity and seeking a future-like position without actually trading futures.',
        'Suitable for traders comfortable with the risks of both short calls and long puts.',
        'Utilized for capitalizing on substantial downward movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential similar to shorting a future, but also carries unlimited upside risk.',
        'Risk managed by selecting appropriate strike prices and being prepared for potential price increases.',
        'Requires active monitoring and understanding of market trends.',
      ],
      Considerations: [
        "Important to have a thorough grasp of market dynamics and the underlying asset's potential for decline.",
        "Influenced by the asset's price volatility and broader market trends.",
        'Practical advice: Stay vigilant about market trends and be ready for potential adjustments.',
      ],
    },
    other_names: 'Synthetic Short Future',
    delta_sensitivity: 'High',
    delta_rationale: 'Synthetic strategy, high delta exposure mimicking stock.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Synthetic position, high gamma risk.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Negative',
    theta_rationale: 'Synthetic short, affected by time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Synthetic short, high Vega risk.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Synthetic position affects Rho moderately.',
  },
  {
    strategy: 'Short Guts Spread',
    slug: 'short-guts',
    subcategory: 'Range-Bound',
    skill_level: 'Expert',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Selling ITM options; high risk and high margin requirement.',
    structure: '- Sell a call at a higher strike\n- Sell a put at a lower strike',
    description: {
      Overview: [
        'A high-risk, premium collection strategy involving selling in-the-money call and put options.',
        'Aims to profit from premium collection if the stock price remains within a certain range.',
        'Suitable for scenarios where moderate stock price movements are expected.',
      ],
      Usage: [
        'Ideal in markets with limited expected volatility, where the stock is anticipated to stay within a specific range.',
        'Suitable for experienced traders comfortable with taking on substantial risk for premium collection.',
        'Utilized for capturing premium income with a defined risk if the market moves significantly.',
      ],
      'Risk Profile': [
        'Offers limited profit potential from the premiums collected, but carries significant risk if the stock moves beyond the strikes.',
        'High risk due to the nature of selling in-the-money options.',
        'Risk managed by carefully monitoring market conditions and being prepared for adjustments.',
      ],
      Considerations: [
        'Requires an understanding of option pricing, market trends, and the potential for price movement.',
        'Influenced by stock price stability and overall market volatility.',
        'Key advice: Be prepared for potential adjustments and close monitoring of the underlying stock.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Aggressive strategy with significant delta exposure.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Aggressive strategy, high gamma exposure.',
    theta_sensitivity: 'Low',
    theta_impact: 'Negative',
    theta_rationale: 'Short ITM options, less affected by time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Short ITM options, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Short ITM options, moderate Rho sensitivity.',
  },
  {
    strategy: 'Short Put',
    slug: 'short-put',
    subcategory: 'Naked',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Bullish, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Nearly Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Requires significant margin for potential stock purchase.',
    structure: '- Sell a put at a higher strike',
    description: {
      Overview: [
        'A bullish strategy involving selling a put option.',
        'Suitable for scenarios where an increase or minimal decrease in the stock price is anticipated.',
        'Traders sell puts hoping the stock will remain above the strike price or rise.',
      ],
      Usage: [
        'Ideal for bullish or neutral investors who do not expect a stock to decline significantly.',
        'Suitable for traders with a high-risk tolerance and a clear bullish or neutral outlook.',
        'Utilized as a way to generate income through premium collection.',
      ],
      'Risk Profile': [
        'Limited profit potential to the premium received, but carries significant risk if the stock price falls sharply.',
        'Risk is managed by selecting appropriate strike prices and expiration dates.',
        "Requires an understanding of the stock's potential and market conditions.",
      ],
      Considerations: [
        "Important to understand the stock's potential for decline and be prepared for the obligation to buy the stock if it falls below the strike price.",
        'Influenced by stock price volatility and market trends.',
        'Practical advice: Monitor the underlying stock closely and be ready for potential adjustments.',
      ],
    },
    other_names: 'Naked Put',
    delta_sensitivity: 'High',
    delta_rationale: "Direct exposure to stock's price decrease, high delta risk.",
    gamma_sensitivity: 'High',
    gamma_rationale: 'Direct exposure to gamma with short put.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short position benefits from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short position benefits from Vega decrease.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short position, low sensitivity to Rho.',
  },
  {
    strategy: 'Short Put Butterfly',
    slug: 'short-put-butterfly',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Sold and bought puts balance out the capital requirement.',
    structure:
      '- Sell a put at a lower strike\n- Buy two puts at the current price\n- Sell a put at a higher strike',
    description: {
      Overview: [
        'A market-neutral strategy that involves selling and buying put options at three different strike prices.',
        'Combines selling one in-the-money put, buying two at-the-money puts, and selling one out-of-the-money put.',
        'Targets a specific price range where the stock is expected to remain at expiration.',
      ],
      Usage: [
        'Best in stable markets with little expected volatility.',
        'Suitable for traders seeking to profit from a stock remaining within a narrow price range.',
        'Utilized for income generation with defined risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a controlled risk environment.',
        'Lower risk compared to aggressive or directional strategies.',
        'Risk management focuses on accurate price target predictions and time decay.',
      ],
      Considerations: [
        'Requires understanding of complex option combinations and market stability.',
        'Sensitive to significant price movements and volatility.',
        'Key advice: Choose strike prices carefully and monitor for unexpected market shifts.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Balanced strategy with limited delta sensitivity.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Balanced position, limited gamma exposure.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short puts in center benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Short center puts reduce Vega risk.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short puts in strategy reduce Rho impact.',
  },
  {
    strategy: 'Short Put Condor',
    slug: 'short-put-condor',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Income, Very Bullish, Very Bearish, Volatile',
    risk: 'Low',
    profit: 'Capped',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Involves short and long puts; moderate capital for strategy.',
    structure:
      '- Buy a put at a lower strike\n- Sell a put at an even lower strike\n- Buy a put at a higher strike\n- Sell a put at an even higher strike',
    description: {
      Overview: [
        'A neutral strategy similar to the put butterfly but with a wider profit range.',
        'Involves selling one lower strike put, buying one lower middle strike put, buying one higher middle strike put, and selling one higher strike put.',
        'Aims to profit from the stock price staying within a broader range compared to a butterfly spread.',
      ],
      Usage: [
        'Ideal in stable markets where significant price movement is not expected.',
        'Suitable for traders looking to profit from limited stock movement within a wider range.',
        'Utilized for generating income with controlled risk.',
      ],
      'Risk Profile': [
        'Limited profit and loss potential, offering a more flexible risk environment compared to a butterfly spread.',
        'Risk managed by balancing the strike prices and expiration dates of the puts.',
        'Lower risk compared to more aggressive directional strategies.',
      ],
      Considerations: [
        'Requires understanding of complex option structures and market behavior.',
        'Influenced by the stability of the underlying stock and overall market conditions.',
        'Key advice: Be prepared for active management and adjustments based on market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Low',
    delta_rationale: 'Limited delta sensitivity within a specific range.',
    gamma_sensitivity: 'Low',
    gamma_rationale: 'Limited gamma sensitivity in a range.',
    theta_sensitivity: 'Moderate',
    theta_impact: 'Positive',
    theta_rationale: 'Short puts benefit from time decay.',
    vega_sensitivity: 'Moderate',
    vega_rationale: 'Limited Vega exposure in structure.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options structure, low Rho.',
  },
  {
    strategy: 'Short Straddle',
    slug: 'short-straddle',
    subcategory: 'Range-Bound',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale:
      'Selling a call and put on the same stock; high margin requirements.',
    structure: '- Sell a put at the current price\n- Sell a call at the current price',
    description: {
      Overview: [
        'A high-risk strategy involving selling an at-the-money call and an at-the-money put on the same stock.',
        'Targets profit from premium collection when the stock remains stable around the strike price.',
        'Suitable for scenarios where little to no movement in the stock price is expected.',
      ],
      Usage: [
        'Ideal for markets with low volatility and minimal expected price movement.',
        'Suitable for experienced traders comfortable with high-risk positions.',
        'Utilized for maximizing income through premium collection.',
      ],
      'Risk Profile': [
        'Offers limited profit potential to the premiums received, but carries significant risk if the stock moves substantially.',
        'High risk due to the potential for large losses on significant price moves in either direction.',
        'Risk managed by careful monitoring of the stock and market conditions.',
      ],
      Considerations: [
        'Requires a thorough understanding of market dynamics and the underlying stock.',
        'Influenced by stock price stability and broader market trends.',
        'Key advice: Stay vigilant and be prepared for swift adjustments.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Highly sensitive to any significant move in the underlying asset.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'High gamma risk with movement in either direction.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short options benefit significantly from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short options, benefit from decrease in Vega.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short options, low overall Rho impact.',
  },
  {
    strategy: 'Short Strangle',
    slug: 'short-strangle',
    subcategory: 'Range-Bound',
    skill_level: 'Advanced',
    market_conditions: 'Income, Neutral, Stable',
    risk: 'High',
    profit: 'Capped',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale: 'Unlimited risk potential; high margin requirement.',
    structure: '- Sell a put at a lower strike\n- Sell a call at a higher strike',
    description: {
      Overview: [
        'A strategy that involves selling an out-of-the-money call and an out-of-the-money put on the same stock.',
        'Aims to profit from premium collection if the stock price stays within a wide range around the strike prices.',
        'Suitable for scenarios expecting little volatility in the stock price.',
      ],
      Usage: [
        'Ideal in stable markets where the stock is anticipated to remain within a specific range.',
        'Suitable for experienced traders comfortable with taking on significant risk for premium collection.',
        'Utilized for capturing premium income within a wider range than a short straddle.',
      ],
      'Risk Profile': [
        'Offers limited profit potential from the premiums collected, but carries significant risk if the stock moves beyond the strikes.',
        'High risk due to the potential for large losses on significant price moves outside the range.',
        'Risk managed by carefully monitoring market conditions and being prepared for adjustments.',
      ],
      Considerations: [
        'Requires an understanding of option pricing, market trends, and the potential for price movement.',
        'Influenced by stock price stability and overall market volatility.',
        'Key advice: Be prepared for potential adjustments and close monitoring of the underlying stock.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Wide exposure to movement in underlying stock, high delta risk.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Wide range, high gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Positive',
    theta_rationale: 'Short positions benefit from time decay.',
    vega_sensitivity: 'Low',
    vega_rationale: 'Short positions reduce overall Vega risk.',
    rho_sensitivity: 'Low',
    rho_rationale: 'Short positions lead to low Rho sensitivity.',
  },
  {
    strategy: 'Short Synthetic Future',
    slug: 'short-synthetic-future',
    subcategory: 'Synthetic',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Nearly Unlimited',
    loss: 'Unlimited',
    capital_requirement: 'High',
    capital_required_rationale:
      'Involves shorting a call and buying a put; significant margin needed.',
    structure: '- Buy a put at the current price\n- Sell a call at the current price',
    description: {
      Overview: [
        'A bearish strategy that mimics a short future position using options, involving selling a call and buying a put at the same strike and expiration.',
        'Designed to profit from a significant downward movement in the stock price.',
        'Suitable for scenarios where a strong bearish trend is anticipated.',
      ],
      Usage: [
        'Ideal for investors bearish on a stock or commodity and seeking a future-like position without actually trading futures.',
        'Suitable for traders comfortable with the risks of both short calls and long puts.',
        'Utilized for capitalizing on substantial downward movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential similar to shorting a future, but also carries unlimited upside risk.',
        'Risk managed by selecting appropriate strike prices and being prepared for potential price increases.',
        'Requires active monitoring and understanding of market trends.',
      ],
      Considerations: [
        "Important to have a thorough grasp of market dynamics and the underlying asset's potential for decline.",
        "Influenced by the asset's price volatility and broader market trends.",
        'Practical advice: Stay vigilant about market trends and be ready for potential adjustments.',
      ],
    },
    other_names: 'Synthetic Short Future',
    delta_sensitivity: 'High',
    delta_rationale: 'Synthetic position, high delta exposure opposite to stock.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Synthetic position, high gamma risk.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Synthetic short, loses on time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Synthetic short, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Synthetic short position, moderate Rho.',
  },
  {
    strategy: 'Straddle',
    slug: 'straddle',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale:
      'Buying both a call and put; high premiums especially for ATM options.',
    structure: '- Buy a put at the current price\n- Buy a call at the current price',
    description: {
      Overview: [
        'A strategy involving buying both a call and a put option at the same strike price and expiration.',
        'Aims to profit from significant movement in either direction of the underlying stock.',
        'Suitable for scenarios where large price swings are anticipated, regardless of direction.',
      ],
      Usage: [
        'Ideal in markets expecting high volatility, such as before major announcements or earnings reports.',
        'Suitable for traders who predict significant movement but are uncertain about the direction.',
        'Utilized for capitalizing on large market movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with risk confined to the premiums paid for both options.',
        'Risk is managed by the dual nature of the position, covering both upward and downward movements.',
        'Requires significant movement to offset the cost of both premiums.',
      ],
      Considerations: [
        'Important to select an appropriate strike price and expiration date.',
        "Influenced by the cost of the options and the stock's volatility.",
        'Practical advice: Monitor the underlying stock closely and be aware of events that could trigger volatility.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Highly sensitive to large movements in either direction.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Highly sensitive to large moves in either direction.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Long positions suffer from time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long positions, high sensitivity to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long positions in both calls and puts.',
  },
  {
    strategy: 'Strangle',
    slug: 'strangle',
    subcategory: 'Bi-Directional',
    skill_level: 'Intermediate',
    market_conditions: 'Growth, Very Bullish, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale: 'Buying OTM options; less premium than straddles.',
    structure: '- Buy a put at a lower strike\n- Buy a call at a higher strike',
    description: {
      Overview: [
        'Similar to a straddle, this strategy involves buying a call and a put option, but with different strike prices.',
        'The call strike is typically higher and the put strike lower than the current stock price.',
        'Aims to profit from significant price movement in either direction, with more flexibility than a straddle.',
      ],
      Usage: [
        'Ideal for markets where a significant price movement is expected, but with greater uncertainty about the direction.',
        'Suitable for traders who want to profit from volatility with potentially lower premiums than a straddle.',
        'Utilized for capturing profits from substantial market movements.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential with risk limited to the premiums paid for both options.',
        'Less expensive than a straddle due to the out-of-the-money positions, but requires more significant movement to be profitable.',
        'Risk managed by covering both potential upward and downward movements.',
      ],
      Considerations: [
        'Requires an understanding of market trends and potential triggers for volatility.',
        'Influenced by the cost of the options and the expected range of stock movement.',
        'Practical advice: Choose strike prices and expirations that align with expected market movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'OTM options, moderate sensitivity to underlying stock movements.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'OTM positions, moderate gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Long options, high sensitivity to time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long positions, high Vega exposure.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long options increase Rho sensitivity.',
  },
  {
    strategy: 'Strap',
    slug: 'strap',
    subcategory: 'Bi-Directional',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bullish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Bullish version of a straddle; high premiums for calls.',
    structure: '- Buy two calls at the current price\n- Buy a put at the current price',
    description: {
      Overview: [
        'A bullish variation of the straddle, this strategy involves buying two call options and one put option at the same strike price and expiration.',
        'Aims to profit from significant upward movement in the stock price, while still covering potential downward movement.',
        'Suitable for scenarios where a significant upward price swing is anticipated, with a hedge against downward movement.',
      ],
      Usage: [
        'Ideal in markets where a strong bullish movement is expected, but with a hedge against bearish volatility.',
        'Suitable for traders predicting a significant increase in stock price with some downside protection.',
        'Utilized for maximizing profits from bullish movements while hedging against declines.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential on the calls with risk confined to the premiums paid.',
        'More bullish bias compared to a straddle, with added protection from the put option.',
        'Requires significant upward movement to offset the cost of the two calls and one put.',
      ],
      Considerations: [
        'Important to choose an appropriate strike price and expiration date that align with market expectations.',
        "Influenced by the cost of the options and the stock's volatility.",
        'Practical advice: Monitor the underlying stock closely and be aware of events that could trigger significant movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Bullish bias increases sensitivity to upward movements.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Bullish bias increases gamma on upward moves.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Long calls increase time decay impact.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long calls increase sensitivity to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long call focused, moderate Rho impact.',
  },
  {
    strategy: 'Strip',
    slug: 'strip',
    subcategory: 'Bi-Directional',
    skill_level: 'Expert',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'High',
    profit: 'Unlimited',
    loss: 'Capped',
    capital_requirement: 'High',
    capital_required_rationale: 'Bearish version of a straddle; high premiums for puts.',
    structure: '- Buy a call at the current price\n- Buy two puts at the current price',
    description: {
      Overview: [
        'A bearish variation of the straddle, this strategy involves buying two put options and one call option at the same strike price and expiration.',
        'Aims to profit from significant downward movement in the stock price, while still covering potential upward movement.',
        'Suitable for scenarios where a significant downward price swing is anticipated, with a hedge against upward movement.',
      ],
      Usage: [
        'Ideal in markets where a strong bearish movement is expected, but with a hedge against bullish volatility.',
        'Suitable for traders predicting a significant decrease in stock price with some upside protection.',
        'Utilized for maximizing profits from bearish movements while hedging against increases.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential on the puts with risk confined to the premiums paid.',
        'More bearish bias compared to a straddle, with added protection from the call option.',
        'Requires significant downward movement to offset the cost of the two puts and one call.',
      ],
      Considerations: [
        'Important to choose an appropriate strike price and expiration date that align with market expectations.',
        "Influenced by the cost of the options and the stock's volatility.",
        'Practical advice: Monitor the underlying stock closely and be aware of events that could trigger significant movements.',
      ],
    },
    other_names: '',
    delta_sensitivity: 'High',
    delta_rationale: 'Bearish bias increases sensitivity to downward movements.',
    gamma_sensitivity: 'High',
    gamma_rationale: 'Bearish bias increases gamma on downward moves.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Long puts increase time decay sensitivity.',
    vega_sensitivity: 'High',
    vega_rationale: 'Long puts increase sensitivity to volatility.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Long put focused, moderate Rho impact.',
  },
  {
    strategy: 'Synthetic Put',
    slug: 'synthetic-put',
    subcategory: 'Synthetic',
    skill_level: 'Advanced',
    market_conditions: 'Growth, Very Bearish, Volatile',
    risk: 'Moderate',
    profit: 'Nearly Unlimited',
    loss: 'Capped',
    capital_requirement: 'Moderate',
    capital_required_rationale:
      'Synthetic position; requires buying stock and put, balanced capital.',
    structure: '- Short the shares\n- Buy a call at the current price',
    description: {
      Overview: [
        'A strategy that mimics a long put position using options and stocks, involving selling a stock and buying a call option at the same strike.',
        'Designed to profit from a significant downward movement in the stock price, similar to owning a put option.',
        'Suitable for scenarios where a bearish trend is anticipated in the underlying stock.',
      ],
      Usage: [
        'Ideal for investors bearish on a stock and seeking a put-like position without actually buying a put option.',
        'Suitable for traders comfortable with the risks of short selling a stock and long calls.',
        'Utilized for capitalizing on substantial downward movements in the stock.',
      ],
      'Risk Profile': [
        'Offers unlimited profit potential similar to a long put, but also carries unlimited upside risk due to the short stock position.',
        'Risk managed by selecting an appropriate strike price for the call and being prepared for potential price increases.',
        'Requires active monitoring and understanding of market trends.',
      ],
      Considerations: [
        "Important to have a thorough understanding of market dynamics and the stock's potential for decline.",
        "Influenced by the stock's price volatility and broader market trends.",
        'Practical advice: Stay vigilant about market trends and be ready for potential adjustments.',
      ],
    },
    other_names: 'Protective Call',
    delta_sensitivity: 'Moderate',
    delta_rationale: 'Synthetic position, moderate delta sensitivity opposite to stock.',
    gamma_sensitivity: 'Moderate',
    gamma_rationale: 'Synthetic position, moderate gamma exposure.',
    theta_sensitivity: 'High',
    theta_impact: 'Negative',
    theta_rationale: 'Synthetic position, sensitive to time decay.',
    vega_sensitivity: 'High',
    vega_rationale: 'Synthetic position, high Vega sensitivity.',
    rho_sensitivity: 'Moderate',
    rho_rationale: 'Synthetic position, moderate Rho sensitivity.',
  },
];
